import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { loadGoogleMaps } from "@/lib/loadGoogleMaps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AddressAutocomplete — uses Google Maps JS SDK (Places library) directly.
 * Faster than a backend proxy, key is restricted by HTTP referrer in Google Cloud Console.
 *
 * Props:
 *  - value: string (controlled)
 *  - onChange: (text: string) => void
 *  - onSelect?: (sug: { place_id, description, main_text, secondary_text }) => void
 *  - placeholder, id, className, autoFocus, error, "data-testid"
 */
/**
 * Extract the postal-code + city + country portion from a Google Places description.
 * "10 Rue de la Paix, 75002 Paris, France" → "75002 Paris, France"
 * Falls back to the original secondary_text if no postal code is detected.
 */
function buildSecondary(description, main, secondary) {
  if (description && main) {
    const idx = description.indexOf(main);
    if (idx === 0) {
      const rest = description.slice(main.length).replace(/^[\s,·-]+/, "");
      if (rest) return rest;
    }
  }
  // If the existing secondary already contains a 5-digit postal code, use it as-is
  if (secondary && /\b\d{5}\b/.test(secondary)) return secondary;
  // Otherwise, try to extract a postal code from the description directly
  if (description) {
    const m = description.match(/\b(\d{5})\b\s+([^,]+)(?:,\s*([^,]+))?/);
    if (m) return m[3] ? `${m[1]} ${m[2]}, ${m[3]}` : `${m[1]} ${m[2]}`;
  }
  return secondary || "";
}

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Tapez une adresse…",
  id,
  className = "",
  autoFocus = false,
  error = false,
  "data-testid": testId = "address-autocomplete",
}) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [ready, setReady] = useState(false);
  const [sdkFailed, setSdkFailed] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const skipNextFetchRef = useRef(false);
  const serviceRef = useRef(null);
  const detailsServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  // Only fetch suggestions after the user has actually typed something.
  // This avoids re-opening the dropdown for values that were prefilled
  // from sessionStorage (e.g. coming from the Hero form).
  const userTypedRef = useRef(false);
  const triggeredRef = useRef(false);
  const [triggered, setTriggered] = useState(false);

  // Lazy-load Google Maps SDK only when user interacts with the input.
  // Saves ~500ms TBT on initial page load (Maps SDK is ~120KB gzipped).
  const triggerMapsLoad = useCallback(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    setTriggered(true);
  }, []);

  // Load Google Maps SDK on first user interaction (focus / typing)
  // with 4s timeout → fallback to backend proxy
  useEffect(() => {
    if (!triggered) return;
    let mounted = true;
    let timeoutId = setTimeout(() => {
      if (mounted && !ready) {
        setSdkFailed(true);
      }
    }, 4000);
    loadGoogleMaps()
      .then((g) => {
        if (!mounted) return;
        clearTimeout(timeoutId);
        try {
          // eslint-disable-next-line no-undef
          serviceRef.current = new g.maps.places.AutocompleteService();
          // eslint-disable-next-line no-undef
          sessionTokenRef.current = new g.maps.places.AutocompleteSessionToken();
          // PlacesService requires a DOM node — use a hidden div
          // eslint-disable-next-line no-undef
          const dummy = document.createElement("div");
          // eslint-disable-next-line no-undef
          detailsServiceRef.current = new g.maps.places.PlacesService(dummy);
          setReady(true);
          setSdkFailed(false);
        } catch (_) {
          setSdkFailed(true);
        }
      })
      .catch(() => {
        if (!mounted) return;
        clearTimeout(timeoutId);
        setSdkFailed(true);
      });
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggered]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Debounced fetch — uses Google SDK if available, else backend proxy fallback
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    // Only fetch suggestions once the user has actually typed in this field.
    // A prefilled value (from sessionStorage etc.) is treated as already validated.
    if (!userTypedRef.current) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);

    const useSdk = ready && serviceRef.current && !sdkFailed;
    const delay = useSdk ? 150 : 250;

    debounceRef.current = setTimeout(async () => {
      if (useSdk) {
        try {
          serviceRef.current.getPlacePredictions(
            {
              input: value,
              sessionToken: sessionTokenRef.current,
              componentRestrictions: { country: "fr" },
              language: "fr",
              types: ["address"],
            },
            (preds) => {
              if (!preds) {
                // SDK returned nothing — switch to proxy for next searches
                setSdkFailed(true);
                proxyFetch(value);
                return;
              }
              const list = preds.map((p) => {
                const main = p.structured_formatting?.main_text || p.description;
                const secondary = p.structured_formatting?.secondary_text || "";
                return {
                  place_id: p.place_id,
                  description: p.description,
                  main_text: main,
                  secondary_text: buildSecondary(p.description, main, secondary),
                };
              });
              setSuggestions(list);
              setOpen(list.length > 0);
              setActiveIdx(-1);
              setLoading(false);
            }
          );
        } catch (_) {
          setSdkFailed(true);
          proxyFetch(value);
        }
      } else {
        proxyFetch(value);
      }
    }, delay);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, ready, sdkFailed]);

  const proxyFetch = useCallback(async (input) => {
    try {
      const res = await axios.post(`${API}/places/autocomplete`, { input });
      const list = (res.data?.suggestions || []).map((s) => {
        const main = s.main_text || s.description;
        return {
          place_id: s.place_id,
          description: s.description,
          main_text: main,
          secondary_text: buildSecondary(s.description, main, s.secondary_text || ""),
        };
      });
      setSuggestions(list);
      setOpen(list.length > 0);
      setActiveIdx(-1);
    } catch (_) {
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = useCallback(
    (sug) => {
      skipNextFetchRef.current = true;
      userTypedRef.current = false;
      // Optimistic: commit the prediction text immediately
      onChange(sug.description);
      onSelect?.(sug);
      setOpen(false);
      setSuggestions([]);
      setActiveIdx(-1);

      // Fetch full formatted address (with postal code) via Place Details
      const svc = detailsServiceRef.current;
      const placeId = sug.place_id;
      if (svc && placeId) {
        try {
          svc.getDetails(
            {
              placeId,
              fields: ["formatted_address", "address_components"],
              sessionToken: sessionTokenRef.current,
            },
            (place, status) => {
              // eslint-disable-next-line no-undef
              if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
                return;
              }
              // Prefer formatted_address (e.g. "10 Rue de la Paix, 75002 Paris, France")
              let full = place.formatted_address || sug.description;
              // Remove trailing ", France" for brevity in form fields
              full = full.replace(/,\s*France\s*$/i, "");
              if (full && full !== sug.description) {
                onChange(full);
                onSelect?.({ ...sug, description: full });
              }
            }
          );
        } catch (_) {
          // ignore
        }
      }
      // New session token after a pick (Google billing best-practice)
      // eslint-disable-next-line no-undef
      if (window.google?.maps?.places) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    },
    [onChange, onSelect]
  );

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        e.preventDefault();
        handleSelect(suggestions[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#237cb5] z-10" />
      <Input
        id={inputId}
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => {
          // First user keystroke → enable suggestion fetching from now on.
          userTypedRef.current = true;
          triggerMapsLoad();
          onChange(e.target.value);
        }}
        onFocus={() => {
          triggerMapsLoad();
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        className={`pl-9 pr-9 h-11 ${error ? "border-[#d92d20] focus-visible:ring-[#d92d20]" : ""} ${className}`}
        aria-autocomplete="list"
        aria-controls={`${inputId}-listbox`}
        aria-expanded={open}
        role="combobox"
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#237cb5] animate-spin" />
      )}

      {open && suggestions.length > 0 && (
        <ul
          id={`${inputId}-listbox`}
          role="listbox"
          data-testid={`${testId}-listbox`}
          className="absolute left-0 right-0 mt-1.5 z-30 max-h-72 overflow-y-auto rounded-xl border border-[#e6edf3] bg-white shadow-[0_24px_40px_-16px_rgba(15,29,43,0.25)]"
        >
          {suggestions.map((s, idx) => (
            <li
              key={s.place_id || idx}
              role="option"
              aria-selected={activeIdx === idx}
              data-testid={`${testId}-option-${idx}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`px-3.5 py-2.5 cursor-pointer border-b border-[#f1f4f7] last:border-b-0 transition-colors ${
                activeIdx === idx ? "bg-[#eaf3fb]" : "bg-white"
              }`}
            >
              <div className="flex items-start gap-2.5">
                <MapPin
                  className={`h-4 w-4 mt-0.5 shrink-0 ${
                    activeIdx === idx ? "text-[#237cb5]" : "text-[#9aa6b2]"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] text-[#0f1d2b] truncate font-medium">
                    <HighlightedAddress text={s.main_text || s.description} query={value} />
                  </div>
                  {s.secondary_text && (
                    <div className="mt-0.5 text-[12px] text-[#5b6b7a] truncate">
                      {s.secondary_text}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
          <li className="px-3.5 py-2 flex items-center justify-end gap-1.5 bg-white border-t border-[#f1f4f7]">
            <span className="text-[10.5px] text-[#5b6b7a]">powered by</span>
            <GoogleLogo />
          </li>
        </ul>
      )}
    </div>
  );
}

// Inline Google logo (SVG, coloured)
function GoogleLogo() {
  return (
    <svg viewBox="0 0 272 92" className="h-3.5 w-auto" aria-hidden="true">
      <path fill="#EA4335" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
      <path fill="#FBBC05" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
      <path fill="#4285F4" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/>
      <path fill="#34A853" d="M225 3v65h-9.5V3h9.5z"/>
      <path fill="#EA4335" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/>
      <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/>
    </svg>
  );
}

// Highlight matched portion of the address text
function HighlightedAddress({ text, query }) {
  if (!query || !query.trim()) return <span>{text}</span>;
  const q = query.trim();
  // Split query into individual tokens and bold each substring found
  const tokens = q.split(/\s+/).filter((t) => t.length >= 1);
  if (tokens.length === 0) return <span>{text}</span>;
  const escaped = tokens.map((t) => t.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"));
  const regex = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((p, i) =>
        regex.test(p) ? (
          <strong key={i} className="font-bold text-[#0f1d2b]">{p}</strong>
        ) : (
          <span key={i} className="text-[#5b6b7a]">{p}</span>
        )
      )}
    </>
  );
}
